import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BadgeCheck, Check, ExternalLink, Pencil, Store, Trash2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API_URL, withCredentials: true });
const imgUrl = (path) => `${API_URL}/brands/images/${path}`;

const STATUS_BADGE = {
  pending: { label: 'Pending review', bg: '#FFF8F0', border: '#FED7A5', color: '#9E6752' },
  approved: { label: 'Approved', bg: '#F0F7F0', border: '#B4C5A9', color: '#4B6043' },
  rejected: { label: 'Rejected', bg: '#FBF0F0', border: '#D4A4A8', color: '#8C4A4F' },
};

const FILTERS = ['pending', 'approved', 'rejected', 'all'];

export default function AdminBrands() {
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null, name: '' });
  const [busyId, setBusyId] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [b, p] = await Promise.all([api.get('/admin/brands'), api.get('/products')]);
      setBrands(b.data);
      setProducts(p.data);
    } catch (e) {
      toast.error('Failed to load brand applications');
    } finally {
      setLoading(false);
    }
  };

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, all: brands.length };
    brands.forEach((b) => { if (c[b.status] !== undefined) c[b.status] += 1; });
    return c;
  }, [brands]);

  const visible = useMemo(
    () => (filter === 'all' ? brands : brands.filter((b) => b.status === filter)),
    [brands, filter]
  );

  const approve = async (brand) => {
    setBusyId(brand.id);
    try {
      const res = await api.post(`/admin/brands/${brand.id}/approve`);
      toast.success(`${brand.brand_name} approved — live at /brands/${res.data.slug}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally {
      setBusyId(null);
    }
  };

  const confirmReject = async () => {
    const brand = rejecting;
    setBusyId(brand.id);
    setRejecting(null);
    try {
      await api.post(`/admin/brands/${brand.id}/reject`, { reason: rejectReason });
      toast.success(`${brand.brand_name} rejected`);
      setRejectReason('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally {
      setBusyId(null);
    }
  };

  const openEdit = (b) => {
    setEditing(b);
    setProductSearch('');
    setForm({
      brand_name: b.brand_name,
      founder_name: b.founder_name || '',
      email: b.email || '',
      website: b.website || '',
      category: b.category || '',
      story: b.story || '',
      slug: b.slug || '',
      linked_product_ids: b.linked_product_ids || [],
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/brands/${editing.id}`, form);
      toast.success('Brand updated');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/brands/${deleteModal.id}`);
      toast.success('Brand deleted');
      setDeleteModal({ isOpen: false, id: null, name: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const copyBadgeEmbed = async (b) => {
    const origin = process.env.REACT_APP_BACKEND_URL;
    const snippet = `<a href="${origin}/brands/${b.slug}" target="_blank" rel="noopener"><img src="${origin}/api/brands/badge.svg?theme=light" alt="Featured on Mementa" width="210" height="54" style="border:0;" /></a>`;
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success('Badge embed code copied — send it to the founder or paste it anywhere');
    } catch {
      toast.error('Copy failed');
    }
  };

  const toggleProduct = (id) => {
    setForm((f) => ({
      ...f,
      linked_product_ids: f.linked_product_ids.includes(id)
        ? f.linked_product_ids.filter((p) => p !== id)
        : [...f.linked_product_ids, id],
    }));
  };

  const filteredProducts = useMemo(() => {
    const q = productSearch.toLowerCase().trim();
    if (!q) return products.slice(0, 30);
    return products.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 30);
  }, [products, productSearch]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="admin-brands-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#20212B' }}>
            <Store size={22} style={{ color: '#534145' }} /> Brand Directory
          </h1>
          <p className="text-sm mt-1" style={{ color: '#9E6752' }}>
            Review Support-a-Brand applications. Approved brands go live at /brands/[slug].
          </p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`brands-filter-${f}`}
              className="px-3.5 py-1.5 rounded-full text-xs font-semibold capitalize transition-colors"
              style={{
                background: filter === f ? '#534145' : '#FFF8F0',
                color: filter === f ? '#FFFAF5' : '#534145',
                border: '1px solid #53414533',
              }}
            >
              {f} ({counts[f]})
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm py-16 text-center" style={{ color: '#9E6752' }}>Loading…</div>
      ) : visible.length === 0 ? (
        <div className="rounded-2xl p-14 text-center bg-white border" style={{ borderColor: '#FED7A5' }} data-testid="brands-admin-empty">
          <Store size={36} className="mx-auto mb-3" style={{ color: '#9E6752', opacity: 0.5 }} />
          <p className="text-sm" style={{ color: '#534145' }}>
            {filter === 'pending' ? 'No pending applications — all caught up!' : `No ${filter === 'all' ? '' : filter} brands yet.`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {visible.map((b) => {
            const badge = STATUS_BADGE[b.status] || STATUS_BADGE.pending;
            return (
              <div
                key={b.id}
                data-testid={`brand-row-${b.id}`}
                className="rounded-2xl bg-white border p-4 sm:p-5 flex flex-col lg:flex-row gap-4"
                style={{ borderColor: b.status === 'pending' ? '#FED7A5' : '#E5E0DA' }}
              >
                {/* Photos */}
                <div className="flex gap-2 flex-shrink-0">
                  {(b.image_paths || []).slice(0, 3).map((p) => (
                    <img key={p} src={imgUrl(p)} alt="" className="rounded-lg object-cover" style={{ width: 84, height: 84 }} />
                  ))}
                  {(!b.image_paths || b.image_paths.length === 0) && (
                    <div className="rounded-lg flex items-center justify-center" style={{ width: 84, height: 84, background: '#F7F0EA' }}>
                      <Store size={22} style={{ color: '#9E6752', opacity: 0.5 }} />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-bold text-base" style={{ color: '#20212B' }}>{b.brand_name}</h3>
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full"
                      style={{ background: badge.bg, border: `1px solid ${badge.border}`, color: badge.color }}>
                      {badge.label}
                    </span>
                    {b.category && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: '#F7F0EA', color: '#534145' }}>{b.category}</span>
                    )}
                  </div>
                  <p className="text-xs mb-1.5" style={{ color: '#9E6752' }}>
                    {b.founder_name} · {b.email}
                    {b.website && (
                      <>
                        {' · '}
                        <a href={b.website} target="_blank" rel="noopener noreferrer" className="underline">{b.website.replace(/^https?:\/\//, '')}</a>
                      </>
                    )}
                  </p>
                  <p className="text-sm line-clamp-2" style={{ color: '#534145' }}>{b.story}</p>
                  {b.status === 'approved' && b.slug && (
                    <a
                      href={`/brands/${b.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs mt-1.5 underline"
                      style={{ color: '#4B6043' }}
                      data-testid={`brand-view-live-${b.id}`}
                    >
                      /brands/{b.slug} <ExternalLink size={11} />
                    </a>
                  )}
                  {b.status === 'rejected' && b.rejection_reason && (
                    <p className="text-xs mt-1.5 italic" style={{ color: '#8C4A4F' }}>Reason: {b.rejection_reason}</p>
                  )}
                  {(b.linked_product_ids || []).length > 0 && (
                    <p className="text-xs mt-1" style={{ color: '#9E6752' }}>{b.linked_product_ids.length} linked catalog product(s)</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex lg:flex-col gap-2 flex-shrink-0 items-start">
                  {b.status !== 'approved' && (
                    <Button
                      size="sm"
                      onClick={() => approve(b)}
                      disabled={busyId === b.id}
                      data-testid={`brand-approve-${b.id}`}
                      className="text-white"
                      style={{ background: '#4B6043' }}
                    >
                      <Check size={14} className="mr-1" /> Approve
                    </Button>
                  )}
                  {b.status !== 'rejected' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setRejecting(b); setRejectReason(''); }}
                      disabled={busyId === b.id}
                      data-testid={`brand-reject-${b.id}`}
                      style={{ borderColor: '#D4A4A8', color: '#8C4A4F' }}
                    >
                      <X size={14} className="mr-1" /> Reject
                    </Button>
                  )}
                  {b.status === 'approved' && b.slug && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyBadgeEmbed(b)}
                      data-testid={`brand-badge-${b.id}`}
                      title='Copy "Featured on Mementa" badge embed code'
                      style={{ borderColor: '#B4C5A9', color: '#4B6043' }}
                    >
                      <BadgeCheck size={14} />
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(b)} data-testid={`brand-edit-${b.id}`}>
                    <Pencil size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDeleteModal({ isOpen: true, id: b.id, name: b.brand_name })}
                    data-testid={`brand-delete-${b.id}`}
                    style={{ color: '#8C4A4F' }}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {editing?.brand_name}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold" style={{ color: '#534145' }}>Brand name</label>
                  <Input value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} data-testid="brand-edit-name" />
                </div>
                <div>
                  <label className="text-xs font-semibold" style={{ color: '#534145' }}>Founder</label>
                  <Input value={form.founder_name} onChange={(e) => setForm({ ...form, founder_name: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold" style={{ color: '#534145' }}>Email</label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold" style={{ color: '#534145' }}>Website</label>
                  <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold" style={{ color: '#534145' }}>Category</label>
                  <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold" style={{ color: '#534145' }}>Slug (URL)</label>
                  <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-set on approval" data-testid="brand-edit-slug" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold" style={{ color: '#534145' }}>Story</label>
                <Textarea rows={4} value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} />
              </div>

              {/* Linked catalog products */}
              <div>
                <label className="text-xs font-semibold" style={{ color: '#534145' }}>
                  Linked Mementa products ({form.linked_product_ids.length} selected)
                </label>
                <Input
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="Search catalog products…"
                  className="mb-2 mt-1"
                  data-testid="brand-edit-product-search"
                />
                <div className="border rounded-lg max-h-48 overflow-y-auto divide-y" style={{ borderColor: '#E5E0DA' }}>
                  {filteredProducts.map((p) => (
                    <label key={p.id} className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-[#FFF8F0]">
                      <input
                        type="checkbox"
                        checked={form.linked_product_ids.includes(p.id)}
                        onChange={() => toggleProduct(p.id)}
                        data-testid={`brand-link-product-${p.id}`}
                      />
                      <span className="flex-1 truncate" style={{ color: '#20212B' }}>{p.name}</span>
                      <span className="text-xs" style={{ color: '#9E6752' }}>${p.price}</span>
                    </label>
                  ))}
                  {filteredProducts.length === 0 && (
                    <div className="px-3 py-4 text-xs text-center" style={{ color: '#9E6752' }}>No products match</div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={saveEdit} className="text-white" style={{ background: '#534145' }} data-testid="brand-edit-save">
                  Save changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject {rejecting?.brand_name}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: '#534145' }}>
            The founder will receive a polite email. You can include an optional reason:
          </p>
          <Textarea
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Optional — e.g. 'We need clearer product photos to feature you.'"
            data-testid="brand-reject-reason"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRejecting(null)}>Cancel</Button>
            <Button onClick={confirmReject} className="text-white" style={{ background: '#8C4A4F' }} data-testid="brand-reject-confirm">
              Reject & notify
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, id: null, name: '' })}
        onConfirm={doDelete}
        title="Delete brand"
        message={`Permanently delete "${deleteModal.name}" and its photos? This cannot be undone.`}
      />
    </div>
  );
}
